import { useEffect, useMemo, useState } from "react";
import { Sparkles, Clock, Tag } from "lucide-react";
import { api } from "../lib/api";

/**
 * ClientPromoBanner — prominent banner that surfaces the highest-value
 * active promo for the logged-in client, with a live countdown timer.
 *
 * Renders nothing if the client has no active promos. Poll-refreshes every
 * 60s so a redeemed promo disappears without requiring a manual reload.
 */
export default function ClientPromoBanner() {
  const [promos, setPromos] = useState([]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/promos/mine");
        if (alive) setPromos(data.items || []);
      } catch { /* silent */ }
    };
    load();
    const refresh = setInterval(load, 60_000);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      alive = false;
      clearInterval(refresh);
      clearInterval(tick);
    };
  }, []);

  const best = useMemo(() => {
    if (!promos.length) return null;
    // Highest value promo → if tied, the one that expires first (use-it-or-lose-it)
    return [...promos].sort((a, b) => {
      const av = Number(a.amount_eur || 0);
      const bv = Number(b.amount_eur || 0);
      if (bv !== av) return bv - av;
      return new Date(a.expires_at) - new Date(b.expires_at);
    })[0];
  }, [promos]);

  if (!best) return null;

  const expiresMs = new Date(best.expires_at).getTime() - now;
  if (expiresMs <= 0) return null;
  const countdown = formatCountdown(expiresMs);
  const label = SOURCE_LABELS[best.source] || "Promoção";

  return (
    <div className="relative overflow-hidden rounded-sm luxstay-promo-banner"
         data-testid="client-promo-banner">
      {/* Animated gold glow ring */}
      <div className="absolute inset-0 luxstay-promo-glow pointer-events-none" aria-hidden="true" />
      {/* Shimmer sweep */}
      <div className="absolute inset-0 luxstay-promo-shimmer pointer-events-none" aria-hidden="true" />
      <div className="relative p-5 md:p-6 flex flex-wrap items-center gap-5">
        <div className="relative w-14 h-14 rounded-sm bg-[#d4af37] text-[#041f1b] flex items-center justify-center shrink-0 luxstay-promo-badge"
             aria-hidden="true">
          <Sparkles size={22} />
          <span className="absolute inset-0 rounded-sm luxstay-promo-ping" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="eyebrow text-[#d4af37] flex items-center gap-2">
            <Tag size={10} /> {label}
          </div>
          <div className="font-display text-2xl md:text-3xl mt-1.5 text-[#fdfbf7] leading-tight">
            <span className="luxstay-promo-value">{formatBenefit(best)}</span>
            <span className="block text-[#a0b2af] text-sm font-sans font-normal mt-1">
              Aplicado automaticamente na sua próxima reserva.
            </span>
          </div>
          <div className="text-xs text-[#a0b2af] mt-3 flex items-center gap-2 flex-wrap">
            <Clock size={12} className="text-[#d4af37]" />
            <span>Termina em</span>
            <span className="font-mono text-[#d4af37] text-sm font-semibold tabular-nums px-2 py-0.5 border border-[#d4af37]/40 rounded-sm bg-[#041f1b]"
                  data-testid="promo-countdown">{countdown}</span>
            {promos.length > 1 && (
              <span className="text-[#d4af37]">· +{promos.length - 1} disponível{promos.length - 1 === 1 ? "" : "is"}</span>
            )}
          </div>
        </div>
        <a href="/cliente/agendar"
           className="btn-gold px-5 py-3 text-xs uppercase tracking-widest whitespace-nowrap shrink-0 self-start md:self-center"
           data-testid="promo-cta">
          Usar agora →
        </a>
      </div>
    </div>
  );
}

const SOURCE_LABELS = {
  welcome: "Promoção de boas-vindas · 30 dias",
  welcome_referred: "Boas-vindas (indicado) · 30 dias",
  referral: "Recompensa por indicação",
  manual: "Oferta exclusiva",
};

function formatBenefit(p) {
  if (p.percentage > 0 && Number(p.amount_eur) > 0) {
    return `${p.percentage}% ou ${Number(p.amount_eur).toFixed(2)} € de desconto`;
  }
  if (p.percentage > 0) return `${p.percentage}% de desconto`;
  return `${Number(p.amount_eur).toFixed(2)} € de desconto`;
}

function formatCountdown(ms) {
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  if (days > 0) return `${days}d ${String(hours).padStart(2, "0")}h ${String(mins).padStart(2, "0")}m`;
  if (hours > 0) return `${hours}h ${String(mins).padStart(2, "0")}m ${String(secs).padStart(2, "0")}s`;
  return `${mins}m ${String(secs).padStart(2, "0")}s`;
}
